import { clsx } from "clsx/lite"
import type { ComponentProps } from "react"

import { MarketingAnchor } from "@/components/marketing/lib/anchor"

const sizes = {
  md: "px-3 py-1",
  lg: "px-4 py-2",
}

const interactiveClassName = "cursor-pointer"

const darkLightLinkClassName =
  `${interactiveClassName} bg-foreground text-background hover:bg-foreground/85`
const lightLinkClassName = `${interactiveClassName} bg-card text-foreground hover:bg-accent`
const softLinkClassName =
  `${interactiveClassName} bg-foreground/10 text-foreground hover:bg-foreground/15`
const plainDarkLightClassName = `${interactiveClassName} text-foreground hover:bg-foreground/10`
const plainLightClassName = `${interactiveClassName} text-background hover:bg-background/15`

export function Button({
  size = "md",
  type = "button",
  color = "dark/light",
  className,
  ...props
}: {
  size?: keyof typeof sizes
  color?: "dark/light" | "light"
} & ComponentProps<"button">) {
  return (
    <button
      type={type}
      className={clsx(
        "inline-flex shrink-0 items-center justify-center gap-1 rounded-full text-sm/7 font-medium",
        color === "dark/light" && darkLightLinkClassName,
        color === "light" && lightLinkClassName,
        sizes[size],
        className,
      )}
      {...props}
    />
  )
}

export function ButtonLink({
  size = "md",
  color = "dark/light",
  className,
  href,
  ...props
}: {
  href: string
  size?: keyof typeof sizes
  color?: "dark/light" | "light"
} & Omit<ComponentProps<"a">, "href">) {
  return (
    <MarketingAnchor
      href={href}
      className={clsx(
        "inline-flex shrink-0 items-center justify-center gap-1 rounded-full text-sm/7 font-medium",
        color === "dark/light" && darkLightLinkClassName,
        color === "light" && lightLinkClassName,
        sizes[size],
        className,
      )}
      {...props}
    />
  )
}

export function SoftButton({
  size = "md",
  type = "button",
  className,
  ...props
}: {
  size?: keyof typeof sizes
} & ComponentProps<"button">) {
  return (
    <button
      type={type}
      className={clsx(
        "inline-flex shrink-0 items-center justify-center gap-1 rounded-full text-sm/7 font-medium",
        softLinkClassName,
        sizes[size],
        className,
      )}
      {...props}
    />
  )
}

export function SoftButtonLink({
  size = "md",
  href,
  className,
  ...props
}: {
  href: string
  size?: keyof typeof sizes
} & Omit<ComponentProps<"a">, "href">) {
  return (
    <MarketingAnchor
      href={href}
      className={clsx(
        "inline-flex shrink-0 items-center justify-center gap-1 rounded-full text-sm/7 font-medium",
        softLinkClassName,
        sizes[size],
        className,
      )}
      {...props}
    />
  )
}

export function PlainButton({
  size = "md",
  color = "dark/light",
  type = "button",
  className,
  ...props
}: {
  size?: keyof typeof sizes
  color?: "dark/light" | "light"
} & ComponentProps<"button">) {
  return (
    <button
      type={type}
      className={clsx(
        "inline-flex shrink-0 items-center justify-center gap-2 rounded-full text-sm/7 font-medium",
        color === "dark/light" && plainDarkLightClassName,
        color === "light" && plainLightClassName,
        sizes[size],
        className,
      )}
      {...props}
    />
  )
}

export function PlainButtonLink({
  size = "md",
  color = "dark/light",
  href,
  className,
  ...props
}: {
  href: string
  size?: keyof typeof sizes
  color?: "dark/light" | "light"
} & Omit<ComponentProps<"a">, "href">) {
  return (
    <MarketingAnchor
      href={href}
      className={clsx(
        "inline-flex shrink-0 items-center justify-center gap-2 rounded-full text-sm/7 font-medium",
        color === "dark/light" && plainDarkLightClassName,
        color === "light" && plainLightClassName,
        sizes[size],
        className,
      )}
      {...props}
    />
  )
}
